import { useEffect } from "react";
import { Link, useRoute } from "wouter";
import { useQuery } from "@tanstack/react-query";
import {
  CalendarDays,
  ChevronLeft,
  MapPin,
  Navigation,
  Users,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { api, ApiError } from "@/lib/api";

type PublicEvent = {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  startDate: string;
  endDate: string | null;
  location: string | null;
  format: "singles" | "doubles" | "mixed";
  latitude: number | null;
  longitude: number | null;
  orgName: string;
  orgSlug: string;
};

// Cross-org public event landing. No auth. The matching SSR layer in
// app.ts injects og + twitter + JSON-LD on first hit so social previews
// work; this React view is the in-app render.
export default function PublicEvent() {
  const [, params] = useRoute("/events/:orgSlug/:eventSlug");
  const orgSlug = params?.orgSlug ?? null;
  const eventSlug = params?.eventSlug ?? null;

  const { data: event, isLoading, error } = useQuery({
    queryKey: ["events-public", orgSlug, eventSlug],
    queryFn: () =>
      api<PublicEvent>(`/events-public/${orgSlug}/${eventSlug}`, {
        public: true,
      }),
    enabled: !!orgSlug && !!eventSlug,
    retry: (count, e) =>
      e instanceof ApiError && e.status === 404 ? false : count < 2,
  });

  useEffect(() => {
    if (event) {
      document.title = `${event.name} — ${event.orgName} · Cornholio`;
    }
  }, [event]);

  if (isLoading) {
    return <div className="p-8 text-muted-foreground text-sm">Loading…</div>;
  }
  if (error || !event) {
    return (
      <div className="min-h-dvh bg-background flex items-center justify-center p-8">
        <div className="text-center space-y-3 max-w-md">
          <h1 className="text-2xl font-display uppercase tracking-tight">
            Event not found
          </h1>
          <p className="text-sm text-muted-foreground">
            It may have been removed or set private by the host.
          </p>
          <Link href="/events" className="text-primary hover:underline text-sm">
            Browse other events
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-background">
      <header className="border-b border-border">
        <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8 sm:py-10 space-y-2">
          <Link
            href="/events"
            className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
          >
            <ChevronLeft size={12} /> All events
          </Link>
          <p className="text-xs font-display uppercase tracking-widest text-muted-foreground">
            <Link
              href={`/${event.orgSlug}`}
              className="hover:text-foreground"
            >
              {event.orgName}
            </Link>
          </p>
          <h1 className="text-3xl sm:text-4xl font-display uppercase tracking-tight">
            {event.name}
          </h1>
          <p className="text-sm text-muted-foreground flex flex-wrap gap-x-4 gap-y-1">
            <span className="inline-flex items-center gap-1">
              <CalendarDays size={13} /> {formatRange(event.startDate, event.endDate)}
            </span>
            {event.location && (
              <span className="inline-flex items-center gap-1">
                <MapPin size={13} /> {event.location}
              </span>
            )}
            <span className="text-[10px] font-display uppercase tracking-widest px-2 py-0.5 rounded-full border border-border">
              {event.format}
            </span>
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-8 py-6 space-y-6">
        {event.description && (
          <Card>
            <CardContent className="p-5 text-sm whitespace-pre-line">
              {event.description}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardContent className="p-5 text-sm space-y-2">
            <p className="font-medium inline-flex items-center gap-2">
              <Users size={14} /> Want to play?
            </p>
            <p className="text-muted-foreground">
              Registration is managed by the host. Reach out to{" "}
              <Link
                href={`/${event.orgSlug}`}
                className="text-primary hover:underline"
              >
                {event.orgName}
              </Link>{" "}
              for details.
            </p>
          </CardContent>
        </Card>

        {event.latitude != null && event.longitude != null && (
          <a
            href={`https://www.google.com/maps?q=${event.latitude},${event.longitude}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
          >
            <Navigation size={13} /> Open in Maps
          </a>
        )}

        <footer className="pt-6 border-t border-border text-xs text-muted-foreground">
          <Link href="/" className="hover:text-foreground">
            Powered by cornholio.app
          </Link>
        </footer>
      </main>
    </div>
  );
}

function formatRange(start: string, end: string | null): string {
  const fmt = (s: string) => {
    const [y, m, d] = s.split("-").map(Number);
    if (!y || !m || !d) return s;
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };
  if (!end || end === start) return fmt(start);
  return `${fmt(start)} → ${fmt(end)}`;
}
